import { useEffect, useState } from 'react';

import Modal from '@cartwhl/ui/molecules/Modal';

import useFfmpeg, { ImageEditSettings, ProgressInfo } from 'hooks/useFfmpeg';
import { navigationStore, projectStore } from 'stores';

import { ModalConfig } from '../constants';
import { useImageCache } from '../hooks';
import { EditableImage } from '../types';
import { getImageEditCacheKey, getImageMetadata } from '../utils';
import Cropping from './Cropping';
import FileUpload from './FileUpload';
import ImageFilePicker from './ImageFilePicker';
import MediaCapture from './MediaCapture';

export type ModalStatus =
  | 'file picker'
  | 'media capture'
  | 'cropping'
  | 'upload';

export default function ImageUploadModal({
  close,
  initialStatus,
  initialSourceImageBlob,
}: {
  close: () => void;
  initialStatus: ModalStatus;
  initialSourceImageBlob?: File | null; // Used when file is uploaded before the modal is opened
}) {
  const [status, setStatus] = useState<ModalStatus>(initialStatus);
  const [progress, setProgress] = useState<ProgressInfo | null>(null);

  // Cache of images that have been edited. This is used to avoid re-editing the same image.
  const { addToCache, getFromCache } = useImageCache();

  // Contains the edits the user has made to the image
  const [editSettings, setEditSettings] = useState<ImageEditSettings>();

  // Source image is the image that the user uploads
  const [sourceImageBlob, setSourceImageBlob] = useState<File | null>(
    initialSourceImageBlob ?? null,
  );

  // Prepared image with metadata
  const [preparedImage, setPreparedImage] = useState<EditableImage | null>(
    null,
  );

  // Cropping input image is the image that is used for the cropping UI. It should not include any cropping.
  const [croppingInputImage, setCroppingInputImage] =
    useState<EditableImage | null>(null);

  const { editImage } = useFfmpeg();

  // Load image metadata when source image changes
  useEffect(() => {
    const loadImage = async () => {
      if (!sourceImageBlob) return;

      try {
        const metadata = await getImageMetadata(sourceImageBlob);
        const image: EditableImage = {
          blob: sourceImageBlob,
          filename: sourceImageBlob.name,
          mimeType: sourceImageBlob.type,
          metadata,
        };
        setPreparedImage(image);
        setCroppingInputImage(image);
        addToCache(getImageEditCacheKey(editSettings), image);
      } catch (error) {
        console.error('Failed to load image metadata:', error);
      }
    };

    loadImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceImageBlob]);

  const sanitizeImageEditSettings = (edits: ImageEditSettings) => {
    // Remove any unnecessary edits (e.g., default values)
    const sanitized = { ...edits };

    // If flip is 0 (no rotation), remove it
    if (sanitized.flip === 0) {
      delete sanitized.flip;
    }

    // If mirror is false, remove it
    if (sanitized.mirror === false) {
      delete sanitized.mirror;
    }

    return sanitized;
  };

  const getEditedImage = async (
    edits: ImageEditSettings,
    progressMessage?: string,
  ) => {
    // First check the cache
    const sanitizedEdits = sanitizeImageEditSettings(edits);
    const cacheKey = getImageEditCacheKey(sanitizedEdits);
    const cachedImage = getFromCache(cacheKey);
    if (cachedImage) return cachedImage;

    // If the image is not in the cache, we need to edit it
    const inputImage = preparedImage;
    if (!inputImage) return;

    setProgress({
      percentage: 0,
      time: '00:00',
      message: progressMessage ?? 'Editing image...',
    });

    const outputFileName = `edited${cacheKey}.png`;

    const editedBlob = await editImage({
      inputBlob: inputImage.blob,
      inputFileName: inputImage.filename,
      outputFileName,
      edits: sanitizedEdits,
      onProgress: setProgress,
      progressMessage,
    });

    const metadata = await getImageMetadata(editedBlob);

    const image: EditableImage = {
      blob: editedBlob,
      filename: outputFileName,
      mimeType: editedBlob.type,
      metadata,
    };

    addToCache(cacheKey, image);

    // Clean up
    setProgress(null);

    return image;
  };

  const handleSubmit = async (editSettings: ImageEditSettings) => {
    setEditSettings(editSettings);

    setProgress({
      percentage: 0,
      time: '00:00',
      message: 'Uploading...',
    });

    const outputImage = await getEditedImage(editSettings, 'Uploading...');

    if (!outputImage || !sourceImageBlob) {
      console.error('No output image or source image blob');
      return;
    }

    const originalFilenameNoExtension = sourceImageBlob.name
      .split('.')
      .slice(0, -1)
      .join('.');
    const extension = sourceImageBlob.name.split('.').pop() || 'png';
    const filename = `${originalFilenameNoExtension}.${extension}`;

    const outputFile = new File([outputImage.blob], filename, {
      type: outputImage.mimeType,
    });

    setProgress({
      percentage: 100,
      time: '00:00',
      message: 'Uploading...',
    });

    projectStore.beginMediaUpload(outputFile, 'image');
    navigationStore.closeModal();

    setStatus('upload');
  };

  return (
    <Modal
      open={true}
      title={ModalConfig[status].title}
      maxWidth={ModalConfig[status].maxWidth}
      verticalBleed={false}
      closeOnClickOutside={false}
      fullscreenOnMobile
    >
      {status === 'file picker' && <ImageFilePicker />}

      {status === 'media capture' && (
        <MediaCapture
          close={close}
          next={(file) => {
            setSourceImageBlob(file);
            setStatus('cropping');
          }}
        />
      )}

      {status === 'cropping' && croppingInputImage && (
        <Cropping
          defaultEditValues={editSettings}
          close={close}
          inputImage={croppingInputImage}
          onMirrorOrFlipChange={async (edits) => {
            const update = {
              ...editSettings,
              ...edits,
            };
            setEditSettings(update);
            const editedImage = await getEditedImage(
              { ...update, crop: undefined }, // don't apply cropping inside cropping mode
              'Flipping the image...',
            );

            if (editedImage) {
              setCroppingInputImage(editedImage);
            }
          }}
          next={(edits) => {
            handleSubmit(edits);
          }}
          progress={progress}
        />
      )}

      {status === 'upload' && <FileUpload close={close} />}
    </Modal>
  );
}
